import { useEffect, useRef, useState } from 'react'
import { createRoute } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  useGetTranslationDashboard,
  translationKeys,
} from '@/application/services/translation.service'
import {
  exportTranslations,
  useImportTranslations,
} from '@/application/services/translation-transfer.service'
import { API_BASE_URL } from '@/infrastructure/api/client'
import { useAuthStore } from '@/application/stores/auth.store'
import type { TranslationImportResult } from '@/domains/types'
import { Card, CardContent } from '@/ui/components/common/Card'
import { translationsRoute } from '@/ui/pages/management/translations'
import { TranslationRowActions } from './components/TranslationRowActions'
import { TranslationImportResultDialog } from './components/TranslationImportResultDialog'
import {
  TranslationCompletenessTable,
  type TranslationCompletenessRow,
} from './components/TranslationCompletenessTable'

interface ImportProgress {
  done: number
  total: number
}

function parseEventData<T>(payload: string): T {
  return JSON.parse(payload)
}

export const translationsDashboardRoute = createRoute({
  getParentRoute: () => translationsRoute,
  path: '/',
  component: TranslationsDashboardPage,
})

export function TranslationsDashboardPage() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { data: rows, isLoading } = useGetTranslationDashboard()
  const importMutation = useImportTranslations()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const clientIdRef = useRef(crypto.randomUUID())
  const [exportingId, setExportingId] = useState<string | null>(null)
  const [pendingImportId, setPendingImportId] = useState<string | null>(null)
  const [importingId, setImportingId] = useState<string | null>(null)
  const [importProgress, setImportProgress] = useState<ImportProgress | null>(null)
  const [importResult, setImportResult] = useState<TranslationImportResult | null>(null)

  useEffect(() => {
    const token = useAuthStore.getState().accessToken ?? ''
    const eventSource = new EventSource(
      `${API_BASE_URL}/translations/import/progress/${clientIdRef.current}?token=${encodeURIComponent(token)}`,
    )

    const handleProgress = (event: MessageEvent<string>) => {
      const data = parseEventData<ImportProgress>(event.data)
      setImportProgress(data)
    }

    const handleComplete = (event: MessageEvent<string>) => {
      const data = parseEventData<TranslationImportResult>(event.data)
      setImportResult(data)
      setImportingId(null)
      setImportProgress(null)
      void queryClient.invalidateQueries({ queryKey: translationKeys.dashboard })
    }

    const handleFailed = () => {
      toast.error(t('translations.dashboard_import_error'))
      setImportingId(null)
      setImportProgress(null)
    }

    eventSource.addEventListener('progress', handleProgress)
    eventSource.addEventListener('complete', handleComplete)
    eventSource.addEventListener('failed', handleFailed)

    return () => {
      eventSource.removeEventListener('progress', handleProgress)
      eventSource.removeEventListener('complete', handleComplete)
      eventSource.removeEventListener('failed', handleFailed)
      eventSource.close()
    }
  }, [queryClient, t])

  const handleExport = async (investigationId: string) => {
    try {
      setExportingId(investigationId)
      const blob = await exportTranslations(investigationId)

      const blobUrl = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = blobUrl
      const dateStr = new Date().toISOString().split('T')[0]
      link.download = `translations-${investigationId}-${dateStr}.json`
      document.body.appendChild(link)
      link.click()
      window.URL.revokeObjectURL(blobUrl)
      document.body.removeChild(link)
    } catch {
      toast.error(t('translations.dashboard_export_error'))
    } finally {
      setExportingId(null)
    }
  }

  const handleImportClick = (investigationId: string) => {
    setPendingImportId(investigationId)
    fileInputRef.current?.click()
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    const investigationId = pendingImportId
    event.target.value = ''
    setPendingImportId(null)

    if (!file || !investigationId) return

    if (file.type !== 'application/json' && !file.name.endsWith('.json')) {
      toast.error(t('translations.dashboard_import_invalid_file'))
      return
    }

    setImportingId(investigationId)
    setImportProgress(null)
    importMutation.mutate(
      { investigationId, file, clientId: clientIdRef.current },
      {
        onError: () => {
          toast.error(t('translations.dashboard_import_error'))
          setImportingId(null)
          setImportProgress(null)
        },
      },
    )
  }

  const tableRows: TranslationCompletenessRow[] = (rows ?? []).map((dashboardRow) => ({
    id: dashboardRow.investigationId,
    label: dashboardRow.name,
    locales: dashboardRow.locales,
    linkTo: {
      to: '/manage/translations/$investigationId',
      params: { investigationId: dashboardRow.investigationId },
    },
    actions: (
      <TranslationRowActions
        investigationId={dashboardRow.investigationId}
        isExporting={exportingId === dashboardRow.investigationId}
        isImporting={importingId === dashboardRow.investigationId}
        importProgress={importingId === dashboardRow.investigationId ? importProgress : null}
        onExport={(investigationId) => {
          void handleExport(investigationId)
        }}
        onImport={handleImportClick}
      />
    ),
  }))

  return (
    <div className="space-y-6">
      <Card>
        <CardContent>
          <TranslationCompletenessTable
            labelHeader={t('translations.dashboard_col_investigation')}
            emptyMessage={t('translations.dashboard_empty')}
            isLoading={isLoading}
            rows={tableRows}
          />
        </CardContent>
      </Card>

      <input
        type="file"
        accept=".json,application/json"
        className="hidden"
        ref={fileInputRef}
        onChange={handleFileChange}
      />

      <TranslationImportResultDialog
        result={importResult}
        onOpenChange={(open) => {
          if (!open) setImportResult(null)
        }}
      />
    </div>
  )
}
